import React from 'react';
import { clsx } from 'clsx';
import { PresentationChartLineIcon } from '@heroicons/react/24/outline';
import type { PositionDetail } from '@/types/trades.types';
import { getRateLabel, GMRA_TRADE_TYPES } from '@/types/trades.types';
import { Panel, PanelHeader } from '@/components/ui/Panel';
import { formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import { isTerminalStatus } from './analytics-economics';
import { effectiveRates } from '@/services/trades/effective-rate';

interface RateWaterfallProps {
  trade: PositionDetail;
}

interface WfStep {
  op: string | null;
  label: string;
  value: string;
  hint?: string | null;
  tone?: 'result' | 'neg';
}

/**
 * Rate build-up (prototype `.waterfall` + `.rate-money`). The rate as a visual
 * formula — `BENCHMARK + spread = rate` for floating rebate, a separate
 * fee + benchmark-cost pair for fee mode (per feedback §2b/d there is no single
 * netted "total rate" — fee on par and cost on CV use different bases), and a
 * single resolved step otherwise. All figures off backend Decimal strings.
 *
 * Mirrors the rate convention used elsewhere (TradeTermsTab / KpiStrip):
 * total = benchmark + spread (LR-464). The operator reflects the spread's sign —
 * a positive spread adds ("+"), a special-stock NEGATIVE spread subtracts ("−") —
 * and the magnitude shows as "|n| bps".
 */
export function RateWaterfall({ trade }: RateWaterfallProps) {
  const currency = trade.currency;
  const rateType = trade.rate_type || 'fixed';
  const isFloatingRebate = rateType === 'floating' && trade.rate_entry_mode === 'rebate';
  const isFeeMode = trade.rate_entry_mode === 'fee';
  const benchmarkName = (trade.benchmark_name || '').toUpperCase();
  const spreadBps = trade.spread_bps ?? 0;
  // Mirror KpiStrip's isTerminal — a matured/closed/cancelled position has no
  // live daily accrual, so the $/yr hint on the result step and the "rate × CV
  // → $/day gross" connector must drop (audit N10; the connector quotes a live
  // gross that contradicts the KPI hero's frozen "—"). Single source:
  // analytics-economics.TERMINAL_STATUSES (LR-208 r5 M3).
  const isTerminal = isTerminalStatus(trade.status);

  // LR-628: a floating rebate's build-up must quote the benchmark it is billing
  // on today, not the one snapshotted at booking — otherwise this panel and the
  // trade's own statement disagree. Booked values stand for everything else.
  const { benchmarkRate, totalRate, dailyEarnings, annualEarnings, showingLiveRate } = effectiveRates(trade);

  const hasRate = toDecimalString(totalRate) != null;
  const rateStr = hasRate ? `${formatDecimalString(totalRate, { dp: 2 })}%` : '—';
  const annualStr =
    !isTerminal && toDecimalString(annualEarnings) != null
      ? `${formatDecimalString(annualEarnings, { currency, dp: 0 })}/yr`
      : null;
  const hasFeePercent = toDecimalString(trade.fee_percent) != null;
  const feeStr = hasFeePercent ? `${formatDecimalString(trade.fee_percent, { dp: 2 })}%` : '—';
  const hasBenchmark = toDecimalString(benchmarkRate) != null;
  const benchmarkStr = hasBenchmark
    ? `${formatDecimalString(benchmarkRate, { dp: 2 })}%`
    : '—';

  // GMRA → "repo", GMSLA → "rebate" (F8a). isFloatingRebate is the build-up
  // trigger; the word comes from the trade type.
  const rateKind = GMRA_TRADE_TYPES.includes(trade.trade_type) ? 'repo' : 'rebate';
  const meta = isFeeMode
    ? 'Fee mode'
    : isFloatingRebate
      ? `Floating · ${rateKind} mode`
      : (trade.rate_type_display ?? rateType.charAt(0).toUpperCase() + rateType.slice(1));

  const steps: WfStep[] = isFeeMode
    ? [
        {
          op: null,
          label: 'Fee · on gross',
          value: feeStr,
          hint: 'agreed fee on par',
          tone: 'result',
        },
        trade.pool_accrues_interest
          // The rate the cash earns in its pool: not netted against the fee. Only the
          // pool's live rate says that; the booking snapshot is not what the pool accrues.
          ? {
              op: null,
              label: 'Cash pool',
              value: showingLiveRate ? benchmarkStr : '—',
              hint: 'accrued by the cash pool',
            }
          : {
              op: null,
              label: hasBenchmark
                ? `Cost · ${benchmarkName || 'benchmark'}`
                : 'Cost · benchmark',
              value: benchmarkStr,
              hint: 'rebate on CV',
              tone: 'neg',
            },
      ]
    : isFloatingRebate && benchmarkName
      ? [
          {
            op: null,
            label: `Benchmark · ${benchmarkName}`,
            value: benchmarkStr,
            hint: trade.benchmark_name ? 'floating' : null,
          },
          {
            op: spreadBps >= 0 ? '+' : '−',
            label: 'Spread',
            value: `${Math.abs(spreadBps)} bps`,
            hint: `${formatDecimalString(String(Math.abs(spreadBps) / 100), { dp: 2 })}%`,
            tone: spreadBps < 0 ? 'neg' : undefined,
          },
          { op: '=', label: getRateLabel(trade.trade_type), value: rateStr, hint: annualStr, tone: 'result' },
        ]
      : [
          {
            op: null,
            label: trade.rate_type_display ? `${trade.rate_type_display} rate` : 'Rate',
            value: rateStr,
            hint: annualStr,
            tone: 'result',
          },
        ];

  // Connector: rate ON notional → gross $/day.
  const notionalStr = formatDecimalString(trade.contract_value, { currency });
  const hasNotional = toDecimalString(trade.contract_value) != null;
  const grossStr = formatDecimalString(dailyEarnings, { currency, dp: 2 });
  const hasGross = toDecimalString(dailyEarnings) != null;
  const grossNeg = grossStr.startsWith('-');
  // The connector asserts "total_rate × contract_value → daily gross". That only
  // reconciles for a floating-rebate trade on CASH collateral (rebate gross
  // accrues on contract_value). In fee mode the daily comes from netting; for
  // non-cash it accrues on collateral_value — in both the caption base (CV) would
  // be a lie, so hide it (F8c). Backend unification tracked under LR-399.
  // Also hide on terminal trades — a frozen position quoting a live "/day gross"
  // would contradict the KPI hero's masked "—" (audit N10).
  const connectorReconciles = isFloatingRebate && trade.collateral_type !== 'non_cash';
  const showConnector = hasRate && hasNotional && connectorReconciles && !isTerminal;

  return (
    <Panel>
      <PanelHeader
        icon={<PresentationChartLineIcon className="h-[15px] w-[15px] flex-none text-lr-muted" aria-hidden="true" />}
        title="Rate build-up"
        meta={meta}
      />
      <div className="flex flex-wrap items-stretch">
        {steps.map((s, i) => (
          <div
            key={s.label}
            className={clsx(
              'relative min-w-[120px] flex-1 px-[18px] py-4',
              i > 0 && 'border-l border-lr-border',
              s.tone === 'result' && 'bg-lr-accent-weak',
            )}
          >
            {s.op && (
              <div className="absolute -left-3 top-1/2 z-[2] grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full border border-lr-border-2 bg-lr-surface text-sm font-bold text-lr-muted">
                {s.op}
              </div>
            )}
            <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-lr-muted">
              {s.label}
            </div>
            <div
              className={clsx(
                'lr-num mt-1.5 text-[22px] font-semibold tracking-[-0.4px]',
                s.tone === 'result' ? 'text-lr-accent' : s.tone === 'neg' ? 'text-lr-neg' : 'text-lr-fg',
              )}
            >
              {s.value}
            </div>
            {s.hint && <div className="mt-[3px] text-[11.5px] text-lr-faint">{s.hint}</div>}
          </div>
        ))}
      </div>
      {showConnector && (
        <div className="flex flex-wrap items-center gap-2.5 border-t border-lr-border bg-lr-surface-2 px-[18px] py-3 text-[13.5px]">
          <span className="lr-num font-bold text-lr-fg">{rateStr}</span>
          <span className="text-[12.5px] text-lr-muted">on</span>
          <span className="lr-num font-bold text-lr-fg">{notionalStr}</span>
          <span className="text-base text-lr-faint">→</span>
          <span
            className={clsx(
              'lr-num ml-auto flex items-center text-[15px] font-bold',
              grossNeg ? 'text-lr-neg' : 'text-lr-pos',
            )}
          >
            {hasGross ? (grossNeg ? grossStr : `+${grossStr}`) : '—'}
            <span className="ml-1 text-[11.5px] font-semibold text-lr-faint">/day gross</span>
          </span>
        </div>
      )}
    </Panel>
  );
}
